// src/pages/Helpdesk/Helpdesk.jsx
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { 
  FaEye, 
  FaReply, 
  FaTrash, 
  FaSearch, 
  FaFilter, 
  FaTimes,
  FaClock,
  FaCheckCircle,
  FaSpinner,
  FaExclamationCircle,
  FaUser,
  FaPhone,
  FaEnvelope,
  FaMapMarkerAlt,
  FaTag,
  FaPaperPlane,
  FaDownload,
} from 'react-icons/fa';
import { FaHeadset, FaSyncAlt } from "react-icons/fa";

import { useDispatch, useSelector } from "react-redux";
import { successToast, errorToast } from '../../Utils/toast';
import { openModal } from "../../redux/features/modalSlice";
import Pagination from '../../common/Pagination';
import SearchBar from '../../common/Search';
import { setHelpDeskFilters } from '../../redux/features/filtersSlice';
import CommonBreadcrumb from '../../common/CommonBreadcrumb';
import { 
  useGetAdminTicketsQuery,
  useGetAdminTicketStatsQuery,
  useGetAdminIssueTypesQuery,
  useUpdateAdminTicketStatusMutation,
  useAddAdminReplyMutation,
  useDeleteAdminTicketMutation,
} from "../../redux/services/helpdeskService/helpdesk.Services";

function Helpdesk() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const filter = useSelector((state) => state.filters?.helpDesk || {});
  
  const [statusFilter, setStatusFilter] = useState(filter.status || '');
  const [issueTypeFilter, setIssueTypeFilter] = useState(filter.issue_type || '');
  const [loadingId, setLoadingId] = useState(null);
  const [replyData, setReplyData] = useState({ id: null, reply: '' });
  const [showReplyModal, setShowReplyModal] = useState(false);

  // API Hooks
  const { data, isLoading, isError, refetch } = useGetAdminTicketsQuery({
    page: filter.page || 1,
    limit: filter.limit || 10,
    status: statusFilter,
    issue_type: issueTypeFilter,
    search: filter.search || '',
  });

  const { data: statsData } = useGetAdminTicketStatsQuery();
  const { data: issueTypesData } = useGetAdminIssueTypesQuery();

  const [updateStatus] = useUpdateAdminTicketStatusMutation();
  const [addReply] = useAddAdminReplyMutation();
  const [deleteTicket] = useDeleteAdminTicketMutation();

  const tickets = data?.data || [];
  const pagination = data?.pagination || {};
  const totalPages = pagination?.totalPages || 0;
  const totalItems = pagination?.total || 0;
  const currentPage = pagination?.page || 1;
  const showPagination = totalPages > 1;
  const stats = statsData?.data || {};
  const issueTypes = issueTypesData?.data || [];

  // Get status badge
  const getStatusBadge = (status) => {
    const statusMap = {
      'Open': { class: 'status-open', icon: <FaClock />, label: 'Open' },
      'In Progress': { class: 'status-inprogress', icon: <FaSpinner />, label: 'In Progress' },
      'Resolved': { class: 'status-resolved', icon: <FaCheckCircle />, label: 'Resolved' },
      'Rejected': { class: 'status-rejected', icon: <FaExclamationCircle />, label: 'Rejected' },
      'Closed': { class: 'status-closed', icon: <FaExclamationCircle />, label: 'Closed' },
    };
    const statusInfo = statusMap[status] || statusMap['Open'];
    return <span className={`helpdesk-status-badge ${statusInfo.class}`}>
      {statusInfo.icon} {statusInfo.label}
    </span>;
  };

  // Format date
  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Handle page change
  const handlePageChange = (page) => {
    dispatch(
      setHelpDeskFilters({
        page,
      })
    );
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Handle search
  const handleSearch = (value) => {
    dispatch(
      setHelpDeskFilters({
        search: value,
        page: 1,
      })
    );
  };

  // Handle limit change
  const handleLimitChange = (e) => {
    const newLimit = parseInt(e.target.value);
    dispatch(
      setHelpDeskFilters({
        limit: newLimit,
        page: 1,
      })
    );
  };

  // Handle status filter change
  const handleStatusFilter = (status) => {
    setStatusFilter(status);
    dispatch(
      setHelpDeskFilters({
        page: 1,
      })
    );
  };

  // Handle issue type filter change
  const handleIssueTypeFilter = (issue_type) => {
    setIssueTypeFilter(issue_type);
    dispatch(
      setHelpDeskFilters({
        page: 1,
      })
    );
  };

  // Clear filters
  const clearFilters = () => {
    setStatusFilter('');
    setIssueTypeFilter('');
    dispatch(
      setHelpDeskFilters({
        search: '',
        page: 1,
      })
    );
  };

  // Handle status change
  const handleStatusChange = async (id, status) => {
    if (!window.confirm(`Are you sure you want to change status to "${status}"?`)) {
      return;
    }

    try {
      setLoadingId(id);
      await updateStatus({ id, status }).unwrap();
      successToast(`Status updated to ${status}`);
      refetch();
    } catch (error) {
      errorToast(error?.data?.message || 'Failed to update status');
    } finally {
      setLoadingId(null);
    }
  };

  // Handle reply
  const handleReplyClick = (id) => {
    setReplyData({ id, reply: '' });
    setShowReplyModal(true);
  };

  // Handle reply submit
  const handleReplySubmit = async (e) => {
    e.preventDefault();

    if (!replyData.reply.trim()) {
      errorToast('Please enter a reply');
      return;
    }

    try {
      setLoadingId(replyData.id);
      await addReply({ id: replyData.id, admin_reply: replyData.reply }).unwrap();
      successToast('Reply sent successfully');
      setShowReplyModal(false);
      setReplyData({ id: null, reply: '' });
      refetch();
    } catch (error) {
      errorToast(error?.data?.message || 'Failed to send reply');
    } finally {
      setLoadingId(null);
    }
  };

  // Handle delete
  const handleDelete = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete ticket "${name}"?`)) {
      return;
    }

    try {
      setLoadingId(id);
      await deleteTicket(id).unwrap();
      successToast('Ticket deleted successfully');
      refetch();
    } catch (error) {
      errorToast(error?.data?.message || 'Failed to delete ticket');
    } finally {
      setLoadingId(null);
    }
  };

  // Refresh
  const handleRefresh = () => {
    refetch();
    successToast('Data refreshed successfully!');
  };

  if (isLoading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <h5 className="mt-3">Loading Tickets...</h5>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="text-center py-5">
        <div className="alert alert-danger">
          <h5>Something Went Wrong</h5>
          <p>Unable to load tickets. Please try again.</p>
          <button className="btn btn-primary" onClick={handleRefresh}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* HEADER */}
      {/* <div className="page-header-breadcrumb all_breadcrumb">
        <div className="ms-md-1 mb-1 mb-md-0 ms-0 page_heading_breadcrumb">
          <nav>
            <h4 className="fw-semibold">
              Help Desk Tickets {totalItems > 0 && <span className="badge-custom bg-primary">{totalItems}</span>}
            </h4>
            <ol className="breadcrumb mb-0">
              <li className="breadcrumb-item active">
                <Link to="/">Home</Link>
              </li>
              <li className="breadcrumb-item">Help Desk</li>
            </ol>
          </nav>
        </div>
        <div className="page-title fw-semibold fs-18 mb-0">
          <button
            className="btn btn-primary me-2"
            onClick={handleRefresh}
          >
            🔄 Refresh
          </button>
        </div>
      </div> */}
<CommonBreadcrumb
    title="Help Desk Tickets"
    titleIcon={<FaHeadset className="me-2 text-primary" />}
    count={totalItems}
    breadcrumbs={[
        { label: "Home", link: "/" },
        { label: "Help Desk" },
    ]}
    buttons={[
        {
            text: "Refresh",
            icon: <FaSyncAlt className="me-1" />,
            onClick: handleRefresh,
            className: "btn btn-primary",
        },
    ]}
/>
      {/* STATS */}
      <div className="row g-3 mb-4">
        <div className="col-md-2 col-6">
          <div className="card card_new_all text-white">
            <div className="card-body text-center">
              <h3 className="mb-0">{stats.total || 0}</h3>
              <small>Total</small>
            </div>
          </div>
        </div>
        <div className="col-md-2 col-6">
          <div className="card card_new_all text-white">
            <div className="card-body text-center">
              <h3 className="mb-0">{stats.open || 0}</h3>
              <small>Open</small>
            </div>
          </div>
        </div>
        <div className="col-md-2 col-6">
          <div className="card card_new_all text-white">
            <div className="card-body text-center">
              <h3 className="mb-0">{stats.inProgress || 0}</h3>
              <small>In Progress</small>
            </div>
          </div>
        </div>
        <div className="col-md-2 col-6">
          <div className="card card_new_all text-white">
            <div className="card-body text-center">
              <h3 className="mb-0">{stats.resolved || 0}</h3>
              <small>Resolved</small>
            </div>
          </div>
        </div>
        <div className="col-md-2 col-6">
          <div className="card card_new_all text-white">
            <div className="card-body text-center">
              <h3 className="mb-0">{stats.rejected || 0}</h3>
              <small>Rejected</small>
            </div>
          </div>
        </div>
        <div className="col-md-2 col-6">
          <div className="card card_new_all text-white">
            <div className="card-body text-center">
              <h3 className="mb-0">{stats.closed || 0}</h3>
              <small>Closed</small>
            </div>
          </div>
        </div>
      </div>

      {/* SEARCH AND FILTER SECTION */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="row g-3 align-items-center">
            <div className="col-md-4">
              <SearchBar
                search={filter.search}
                setSearch={handleSearch}
                placeholder="Search by name, mobile, email, ticket ID..."
              />
            </div>

            <div className="col-md-2">
              <select
                className="form-select"
                value={statusFilter}
                onChange={(e) => handleStatusFilter(e.target.value)}
              >
                <option value="">All Status</option>
                <option value="Open">Open</option>
                <option value="In Progress">In Progress</option>
                <option value="Resolved">Resolved</option>
                <option value="Rejected">Rejected</option>
                <option value="Closed">Closed</option>
              </select>
            </div>

            <div className="col-md-2">
              <select
                className="form-select"
                value={issueTypeFilter}
                onChange={(e) => handleIssueTypeFilter(e.target.value)}
              >
                <option value="">All Types</option>
                {issueTypes.map((type) => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            <div className="col-md-2">
              <select
                className="form-select"
                value={filter.limit || 10}
                onChange={handleLimitChange}
              >
                <option value={5}>5 per page</option>
                <option value={10}>10 per page</option>
                <option value={20}>20 per page</option>
                <option value={50}>50 per page</option>
              </select>
            </div>

            <div className="col-md-2 text-md-end">
              {(filter.search || statusFilter || issueTypeFilter) && (
                <button
                  className="btn btn-outline-secondary me-2"
                  onClick={clearFilters}
                >
                  <FaTimes className="me-1" />
                  Clear
                </button>
              )}
            </div>
          </div>

          {/* Active Filters */}
          {(filter.search || statusFilter || issueTypeFilter) && (
            <div className="mt-3 d-flex flex-wrap gap-2">
              <span className="text-muted me-2">Active Filters:</span>
              {filter.search && (
                <span className="badge bg-primary d-flex align-items-center gap-1">
                  Search: {filter.search}
                  <button
                    className="btn btn-link btn-sm text-white p-0 ms-1"
                    onClick={() => handleSearch('')}
                  >
                    <FaTimes />
                  </button>
                </span>
              )}
              {statusFilter && (
                <span className="badge bg-info d-flex align-items-center gap-1">
                  Status: {statusFilter}
                  <button
                    className="btn btn-link btn-sm text-white p-0 ms-1"
                    onClick={() => handleStatusFilter('')}
                  >
                    <FaTimes />
                  </button>
                </span>
              )}
              {issueTypeFilter && (
                <span className="badge bg-success d-flex align-items-center gap-1">
                  Type: {issueTypeFilter}
                  <button
                    className="btn btn-link btn-sm text-white p-0 ms-1"
                    onClick={() => handleIssueTypeFilter('')}
                  >
                    <FaTimes />
                  </button>
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* TABLE */}
      <div className="st-wrapper-card">
        {tickets.length === 0 ? (
          <div className="text-center py-5">
            <div className="mb-3">
              <FaSearch className="text-muted" size={48} />
            </div>
            <h5>No Tickets Found</h5>
            <p className="text-muted">
              {filter.search || statusFilter || issueTypeFilter
                ? "Try adjusting your search or filter criteria"
                : "No support tickets available"}
            </p>
            {(filter.search || statusFilter || issueTypeFilter) && (
              <button className="btn btn-primary" onClick={clearFilters}>
                Clear Filters
              </button>
            )}
          </div>
        ) : (
          <div className="st-table-responsive">
            <table className="table st-custom-table align-middle mb-0">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Ticket ID</th>
                  <th>User</th>
                  <th>Mobile</th>
                  <th>Issue Type</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th className="text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {tickets.map((item, index) => (
                  <tr key={item.id}>
                    <td>{(currentPage - 1) * (filter.limit || 10) + index + 1}</td>
                    <td>
                      <span className="fw-bold text-warning">#{item.ticket_id}</span>
                    </td>
                    <td>
                      <div>
                        <div className="fw-semibold">{item.full_name}</div>
                        {item.email && <small className="text-muted">{item.email}</small>}
                      </div>
                    </td>
                    <td>{item.mobile_number}</td>
                    <td>
                      {item.issue_type && (
                        <span className="badge bg-info">{item.issue_type}</span>
                      )}
                    </td>
                    <td>
                      <select
                        className="form-select form-select-sm"
                        value={item.status}
                        onChange={(e) => handleStatusChange(item.id, e.target.value)}
                        disabled={loadingId === item.id}
                        style={{ minWidth: '120px' }}
                      >
                        <option value="Open">Open</option>
                        <option value="In Progress">In Progress</option>
                        <option value="Resolved">Resolved</option>
                        <option value="Rejected">Rejected</option>
                        <option value="Closed">Closed</option>
                      </select>
                    </td>
                    <td>{formatDate(item.created_at)}</td>
                    <td className="text-center">
                      {/* VIEW */}
                      <button
                        className="st-action-btn st-view-btn me-1"
                        onClick={() =>
                          dispatch(
                            openModal({
                              type: "view-ticket",
                              title: "View Ticket",
                              data: item,
                            })
                          )
                        }
                        title="View Details"
                        disabled={loadingId === item.id}
                      >
                        <FaEye />
                      </button>

                      {/* REPLY */}
                      <button
                        className="st-action-btn st-status-btn me-1"
                        onClick={() => handleReplyClick(item.id)}
                        title="Reply"
                        disabled={loadingId === item.id}
                      >
                        <FaReply />
                      </button>

                      {/* DELETE */}
                      <button
                        className="st-action-btn st-delete-btn"
                        onClick={() => handleDelete(item.id, item.ticket_id)}
                        title="Delete"
                        disabled={loadingId === item.id}
                      >
                        {loadingId === item.id ? (
                          <span className="spinner-border spinner-border-sm" />
                        ) : (
                          <FaTrash />
                        )}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* PAGINATION */}
        {showPagination && tickets.length > 0 && (
          <div className="d-flex flex-wrap align-items-center justify-content-between mt-4">
            <div className="text-muted small mb-2 mb-md-0">
              Showing {((currentPage - 1) * (filter.limit || 10)) + 1} to{' '}
              {Math.min(currentPage * (filter.limit || 10), totalItems)} of {totalItems} entries
            </div>
            <Pagination
              page={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </div>

      {/* ====================== */}
      {/* REPLY MODAL */}
      {/* ====================== */}
      {showReplyModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <FaReply className="me-2" /> Reply to Ticket
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowReplyModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <form onSubmit={handleReplySubmit}>
                  <div className="mb-3">
                    <label className="form-label fw-bold">Your Reply <span className="text-danger">*</span></label>
                    <textarea
                      className="form-control"
                      rows="5"
                      value={replyData.reply}
                      onChange={(e) => setReplyData({ ...replyData, reply: e.target.value })}
                      placeholder="Type your reply here..."
                      required
                    />
                  </div>
                  <div className="d-flex justify-content-end gap-2">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => setShowReplyModal(false)}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={loadingId === replyData.id}
                    >
                      {loadingId === replyData.id ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <FaPaperPlane className="me-2" />
                          Send Reply
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Helpdesk;